/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */

import React, { useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { Link, useRouterActions } from 'react-resource-router';

import { Profile } from '@atlaskit/atlassian-navigation';
import Avatar from '@atlaskit/avatar';
import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx, cx } from '@atlaskit/css';
import HomeIcon from '@atlaskit/icon/core/home';
import InboxIcon from '@atlaskit/icon/core/inbox';
import { fg } from '@atlaskit/platform-feature-flags';
import Popup from '@atlaskit/popup';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { IconColor } from '@atlaskit/tokens/css-type-schema';
import { getHubLogoHeight, useHubContext, useNavigationStyle } from '@atlassian/company-hub-common';

import { AvatarMenu } from '../avatar-menu';

import messages from './messages';
import { NavLinks } from './nav-links';

const styles = cssMap({
	navbar: {
		paddingTop: token('space.300'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.300'),
		paddingLeft: token('space.300'),
	},
	navbarUpdated: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	navBarDisabled: {
		pointerEvents: 'none',
	},
	logoContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
	rightSection: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
	},
});

export const Navbar = ({ isEditMode = false }: { isEditMode?: boolean }): JSX.Element => {
	di(fg, useHubContext);
	const [isAvatarMenuOpen, setIsAvatarMenuOpen] = useState(false);

	const hubContext = useHubContext();
	const { featureFlags, hubType } = hubContext;
	const { options: navStyle } = useNavigationStyle();
	const isNavBarUpdated = featureFlags?.hub_component_upgrade ?? false;

	const dynamicStyle = useMemo<React.CSSProperties | undefined>(() => {
		if (!navStyle) {
			return undefined;
		}
		const border = `1px solid ${navStyle.borderColor}`;

		return {
			backgroundColor: navStyle.backgroundColor,
			color: navStyle.textColor,
			borderRadius: navStyle.padding ? `${navStyle.cornerRadius}px` : '0',
			border: navStyle.borderType === 'ALL_BORDER' ? border : 'none', // this will be the css 'none' value
			borderBottom: ['BOTTOM_BORDER', 'ALL_BORDER'].includes(navStyle.borderType)
				? border
				: undefined, // bottom border is always applied for all borders
			position: navStyle.fixedAtTop ? 'sticky' : 'relative',
			top: navStyle.padding && navStyle.fixedAtTop ? token('space.300') : '0px',
			zIndex: navStyle.fixedAtTop ? 1 : undefined,
			marginTop: navStyle.padding ? token('space.300') : undefined,
			marginLeft: navStyle.padding ? token('space.300') : undefined,
			marginRight: navStyle.padding ? token('space.300') : undefined,
			marginBottom: token('space.300'), // we always add a margin bottom to the navbar
			transition: 'margin 0.3s ease, border-radius 0.3s ease, top 0.3s ease',
		};
	}, [navStyle]);

	const iconColor = isNavBarUpdated ? (navStyle?.textColor as IconColor) : undefined;

	const { push } = useRouterActions();

	const { formatMessage } = useIntl();

	const logoUrl = hubContext.branding.logo.url;
	const { avatarSrc, email } = hubContext.user;
	const { renderRightNavActions } = hubContext;

	const isAnonymousRoute = hubContext.layout?.isAnonymousRoute || false;

	const handleInboxClick = useCallback(() => {
		push(`/user/requests`);
	}, [push]);

	const handleProfileClick = useCallback(() => {
		setIsAvatarMenuOpen(!isAvatarMenuOpen);
	}, [isAvatarMenuOpen]);

	const handleAvatarMenuClose = useCallback(() => {
		setIsAvatarMenuOpen(false);
	}, []);

	return (
		<Box
			data-testId="navbar"
			xcss={cx(
				isNavBarUpdated ? styles.navbarUpdated : styles.navbar,
				isEditMode && styles.navBarDisabled,
			)}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Dynamic navigation styles from context
			style={isNavBarUpdated ? dynamicStyle : {}}
		>
			<Flex justifyContent="space-between" alignItems="center">
				<Box xcss={styles.logoContainer}>
					<Link to={!isAnonymousRoute ? '/' : '/user/login'}>
						{logoUrl ? (
							<img
								height={getHubLogoHeight(hubType)}
								src={logoUrl}
								alt={formatMessage(messages.hubLogo)}
							/>
						) : (
							<IconButton
								icon={(iconProps) => <HomeIcon {...iconProps} color={iconColor} />}
								label={formatMessage(messages.hubLogo)}
								color={navStyle?.textColor}
							/>
						)}
					</Link>
				</Box>
				{!isAnonymousRoute && email && isNavBarUpdated && (
					<NavLinks textColor={navStyle?.textColor} backgroundColor={navStyle?.backgroundColor} />
				)}
				{!isAnonymousRoute && email && (
					<Box xcss={styles.rightSection}>
						{!isNavBarUpdated && (
							<>
								<div>{renderRightNavActions && renderRightNavActions()}</div>
								<IconButton
									appearance="subtle"
									icon={(iconProps) => <InboxIcon {...iconProps} color={iconColor} />}
									label="User requests"
									onClick={handleInboxClick}
								/>
							</>
						)}
						<Popup
							trigger={(triggerProps) => (
								<Profile
									{...triggerProps}
									tooltip="Profile and settings"
									icon={<Avatar appearance="circle" size="medium" src={avatarSrc} />}
									onClick={handleProfileClick}
								/>
							)}
							shouldRenderToParent
							placement="bottom-end"
							isOpen={isAvatarMenuOpen}
							content={() => <AvatarMenu />}
							onClose={handleAvatarMenuClose}
						/>
					</Box>
				)}
				{!isAnonymousRoute && !email && (
					<Link to={`/user/login`}>
						<Button>{formatMessage(messages.login)}</Button>
					</Link>
				)}
			</Flex>
		</Box>
	);
};
